import { Helmet } from "react-helmet-async";

interface OrganizationSchemaProps {
  type?: "Organization" | "LocalBusiness" | "WebApplication";
}

export const OrganizationSchema = ({ type = "Organization" }: OrganizationSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    name: "SKECHA",
    description: "Transform your family photos into personalized coloring pages with AI",
    url: "https://skecha.com",
    logo: "https://skecha.com/uploads/skecha-logo.ico",
    sameAs: [
      "https://twitter.com/lovable_dev",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      availableLanguage: ["English"],
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Melbourne",
      addressRegion: "VIC",
      addressCountry: "AU",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface WebsiteSchemaProps {
  searchUrl?: string;
}

export const WebsiteSchema = ({ searchUrl = "https://skecha.com/gallery" }: WebsiteSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "SKECHA",
    url: "https://skecha.com",
    description: "Transform your photos into beautiful coloring pages",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${searchUrl}?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface ProductSchemaProps {
  name: string;
  description: string;
  price: number;
  priceCurrency?: string;
  availability?: string;
}

export const ProductSchema = ({
  name,
  description,
  price,
  priceCurrency = "USD",
  availability = "https://schema.org/InStock",
}: ProductSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    brand: {
      "@type": "Brand",
      name: "SKECHA",
    },
    offers: {
      "@type": "Offer",
      price,
      priceCurrency,
      availability,
      url: "https://skecha.com/pricing",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
